import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Switch, ScrollView } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { scale } from '@components/frame';
import { StressTestConfig } from './index';

interface StressTestControlsProps {
  config: StressTestConfig;
  onConfigChange: (config: StressTestConfig) => void;
  isRunning: boolean;
  colors: any;
  isPhone: boolean;
}

const StressTestControls: React.FC<StressTestControlsProps> = ({
  config,
  onConfigChange,
  isRunning,
  colors,
  isPhone,
}) => {
  const updateConfig = (updates: Partial<StressTestConfig>) => {
    onConfigChange({ ...config, ...updates });
  };

  const updateWorkarounds = (updates: Partial<StressTestConfig['workarounds']>) => {
    onConfigChange({
      ...config,
      workarounds: { ...config.workarounds, ...updates },
    });
  };

  const updateMetrics = (updates: Partial<StressTestConfig['metrics']>) => {
    onConfigChange({
      ...config,
      metrics: { ...config.metrics, ...updates },
    });
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
    },
    section: {
      marginBottom: scale(20),
    },
    sectionTitle: {
      fontSize: scale(15),
      fontWeight: '700',
      color: colors.text,
      marginBottom: scale(12),
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    row: {
      flexDirection: isPhone ? 'column' : 'row',
      alignItems: isPhone ? 'stretch' : 'center',
      marginBottom: scale(12),
      paddingVertical: scale(8),
    },
    label: {
      flex: isPhone ? 0 : 1,
      fontSize: scale(14),
      color: colors.text,
      marginBottom: isPhone ? scale(8) : 0,
      fontWeight: '500',
    },
    picker: {
      flex: 1,
      height: scale(44),
      color: colors.text,
      backgroundColor: colors.background,
      borderRadius: scale(8),
      borderWidth: 1,
      borderColor: colors.border,
    },
    switchContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: isPhone ? 'space-between' : 'flex-start',
    },
    switch: {
      marginLeft: isPhone ? 0 : scale(8),
    },
    subSection: {
      marginLeft: isPhone ? 0 : scale(24),
      marginTop: scale(8),
      padding: scale(12),
      backgroundColor: colors.background,
      borderRadius: scale(8),
      borderLeftWidth: scale(3),
      borderLeftColor: colors.primary,
    },
    infoText: {
      fontSize: scale(12),
      color: colors.textSecondary,
      fontStyle: 'italic',
      marginTop: scale(4),
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginVertical: scale(16),
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Request Configuration */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Request Configuration</Text>

        <View style={styles.row}>
          <Text style={styles.label}>Concurrent Requests</Text>
          <Picker
            selectedValue={config.concurrentRequests}
            style={styles.picker}
            onValueChange={(value) => updateConfig({ concurrentRequests: value as any })}
            enabled={!isRunning}
          >
            <Picker.Item label="5 requests" value={5} color={colors.text} />
            <Picker.Item label="10 requests" value={10} color={colors.text} />
            <Picker.Item label="20 requests" value={20} color={colors.text} />
            <Picker.Item label="50 requests" value={50} color={colors.text} />
            <Picker.Item label="100 requests" value={100} color={colors.text} />
          </Picker>
        </View>
      </View>

      <View style={styles.divider} />

      {/* Samsung Workarounds */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Samsung Workarounds</Text>

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>setTimeout Delay</Text>
            <Switch
              value={config.workarounds.useSetTimeout}
              onValueChange={(value) => updateWorkarounds({ useSetTimeout: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.workarounds.useSetTimeout ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>

        {config.workarounds.useSetTimeout && (
          <View style={styles.subSection}>
            <Text style={styles.label}>Delay Amount</Text>
            <Picker
              selectedValue={config.workarounds.timeoutDelay}
              style={styles.picker}
              onValueChange={(value) => updateWorkarounds({ timeoutDelay: value })}
              enabled={!isRunning}
            >
              <Picker.Item label="0ms" value={0} color={colors.text} />
              <Picker.Item label="50ms" value={50} color={colors.text} />
              <Picker.Item label="100ms" value={100} color={colors.text} />
              <Picker.Item label="200ms" value={200} color={colors.text} />
              <Picker.Item label="500ms" value={500} color={colors.text} />
            </Picker>
          </View>
        )}

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>InteractionManager</Text>
            <Switch
              value={config.workarounds.useInteractionManager}
              onValueChange={(value) => updateWorkarounds({ useInteractionManager: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.workarounds.useInteractionManager ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>
        <Text style={styles.infoText}>Defers requests until after interactions</Text>

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Request Batching</Text>
            <Switch
              value={config.workarounds.useBatching}
              onValueChange={(value) => updateWorkarounds({ useBatching: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.workarounds.useBatching ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>

        {config.workarounds.useBatching && (
          <View style={styles.subSection}>
            <Text style={styles.label}>Batch Size</Text>
            <Picker
              selectedValue={config.workarounds.batchSize}
              style={styles.picker}
              onValueChange={(value) => updateWorkarounds({ batchSize: value })}
              enabled={!isRunning}
            >
              <Picker.Item label="1 request/batch" value={1} color={colors.text} />
              <Picker.Item label="5 requests/batch" value={5} color={colors.text} />
              <Picker.Item label="10 requests/batch" value={10} color={colors.text} />
            </Picker>
          </View>
        )}
      </View>

      <View style={styles.divider} />

      {/* Performance Metrics */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Tracking</Text>

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Network Timing</Text>
            <Switch
              value={config.metrics.trackNetworkTiming}
              onValueChange={(value) => updateMetrics({ trackNetworkTiming: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.metrics.trackNetworkTiming ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Redux Performance</Text>
            <Switch
              value={config.metrics.trackReduxPerformance}
              onValueChange={(value) => updateMetrics({ trackReduxPerformance: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.metrics.trackReduxPerformance ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>Memory Usage</Text>
            <Switch
              value={config.metrics.trackMemory}
              onValueChange={(value) => updateMetrics({ trackMemory: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.metrics.trackMemory ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.switchContainer}>
            <Text style={styles.label}>JS Thread Blocking</Text>
            <Switch
              value={config.metrics.trackJSThread}
              onValueChange={(value) => updateMetrics({ trackJSThread: value })}
              disabled={isRunning}
              style={styles.switch}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={config.metrics.trackJSThread ? colors.primary : '#f4f3f4'}
            />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default StressTestControls;